import {
	cloneElement,
	isValidElement,
	ReactElement,
	Ref,
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';
import { Tooltip, TooltipProps, Typography, TypographyProps } from '@mui/material';

interface TruncatedTextProps extends Omit<TypographyProps, 'children' | 'noWrap'> {
	/** The full text to display, and the verbatim tooltip content. */
	text: string;
	/**
	 * Number of lines to show before truncating. Defaults to 1 (single-line
	 * ellipsis). Values greater than 1 clamp to that many lines.
	 */
	lines?: number;
	/** Props forwarded to the wrapping tooltip (e.g. placement). */
	tooltipProps?: Partial<Omit<TooltipProps, 'title' | 'children'>>;
	/**
	 * Optional custom element to render and truncate instead of a plain
	 * Typography (e.g. a styled link). It receives the truncation styles and
	 * the overflow ref; the tooltip content still comes from `text`.
	 */
	children?: ReactElement;
}

/**
 * Renders text with an ellipsis when it overflows its container, and reveals
 * the full value in a tooltip on hover. The tooltip is only enabled when the
 * text is actually clipped, so short values stay tooltip-free. Supports
 * single-line (default) and multi-line clamping via the `lines` prop.
 *
 * For a plain value, pass `text`. To truncate custom markup (e.g. a styled
 * link), pass that element as `children` along with `text` for the tooltip.
 */
export const TruncatedText = ({
	text,
	lines = 1,
	tooltipProps,
	sx,
	children,
	...typographyProps
}: TruncatedTextProps) => {
	const ref = useRef<HTMLElement>(null);
	const [isOverflowing, setIsOverflowing] = useState(false);

	const measure = () => {
		const el = ref.current;
		if (!el) return;
		// Single-line clips horizontally; multi-line clamps vertically.
		const overflowing =
			lines > 1 ? el.scrollHeight > el.clientHeight + 1 : el.scrollWidth > el.clientWidth + 1;
		setIsOverflowing(overflowing);
	};

	useLayoutEffect(() => {
		measure();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [text, lines]);

	useEffect(() => {
		const el = ref.current;
		if (!el || typeof ResizeObserver === 'undefined') return;
		const observer = new ResizeObserver(() => measure());
		observer.observe(el);
		return () => observer.disconnect();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const clampSx =
		lines > 1
			? {
					display: '-webkit-box',
					WebkitLineClamp: lines,
					WebkitBoxOrient: 'vertical' as const,
					overflow: 'hidden',
					wordBreak: 'break-word' as const,
				}
			: {
					overflow: 'hidden',
					textOverflow: 'ellipsis',
					whiteSpace: 'nowrap' as const,
				};

	let content: ReactElement;
	if (isValidElement(children)) {
		const child = children as ReactElement<{ ref?: Ref<HTMLElement>; sx?: object }>;
		content = cloneElement(child, {
			ref,
			sx: { minWidth: 0, ...clampSx, ...(child.props.sx ?? {}) },
		});
	} else {
		content = (
			<Typography ref={ref} {...typographyProps} sx={{ minWidth: 0, ...clampSx, ...sx }}>
				{text}
			</Typography>
		);
	}

	if (!isOverflowing) return content;

	return (
		<Tooltip title={text} {...tooltipProps}>
			{content}
		</Tooltip>
	);
};
